/**
 * Creative Review — dialog behaviour for the review sheet.
 *
 * This file must stay byte-identical between the client and superadmin apps.
 *
 * The review surface is a real modal dialog, but it is rendered inline as the
 * last child of the Creative Studio page rather than through a portal. That
 * placement is what makes the helpers here necessary:
 *
 * - The page-level floating chrome (the Ask pill, the Eden orb, the previous
 *   page arrow) is fixed at a z-index far above the modal and lives outside the
 *   page root entirely, so it cannot be layered away from inside the modal. A
 *   body-level attribute is the only signal both can see, and hiding by
 *   `visibility` rather than `display` keeps those controls' own state and
 *   animations intact while making them untappable — raising the modal's
 *   z-index alone would leave invisible controls still taking taps.
 * - The background cannot be hidden from assistive technology by marking the
 *   page root, because the dialog is inside it. The dialog's siblings are
 *   marked instead.
 *
 * Everything here is pure DOM manipulation with an explicit restore function so
 * that opening and closing is symmetrical, and so the rules are testable
 * without mounting the page.
 */

export const CREATIVE_REVIEW_OPEN_ATTRIBUTE = "data-creative-review-open";

/**
 * Elements that can hold focus. `[tabindex="-1"]` is excluded deliberately: it
 * marks a programmatic focus target, not a stop in the tab sequence.
 */
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button",
  "input",
  "select",
  "textarea",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export function getFocusableElements(container) {
  if (!container || typeof container.querySelectorAll !== "function") return [];
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (element) =>
      !element.hasAttribute("disabled") &&
      element.getAttribute("aria-hidden") !== "true" &&
      element.getAttribute("tabindex") !== "-1"
  );
}

/**
 * Where Tab should land, or null to let the browser move focus itself.
 *
 * Only the two wrap points are claimed — leaving the interior to the browser
 * keeps the sheet's natural reading order intact instead of re-deriving it.
 */
export function resolveFocusWrapTarget(container, activeElement, shiftKey) {
  const focusable = getFocusableElements(container);
  if (!focusable.length) return null;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const holdsFocus = Boolean(
    container && activeElement && container.contains(activeElement)
  );
  if (shiftKey) {
    return !holdsFocus || activeElement === first ? last : null;
  }
  return !holdsFocus || activeElement === last ? first : null;
}

/**
 * Flags the document as having the review sheet open, which is what suppresses
 * the floating page chrome. Returns the restore function.
 */
export function markCreativeReviewOpen(documentRef) {
  const body = documentRef && documentRef.body;
  if (!body) return () => {};
  body.setAttribute(CREATIVE_REVIEW_OPEN_ATTRIBUTE, "true");
  return () => {
    body.removeAttribute(CREATIVE_REVIEW_OPEN_ATTRIBUTE);
  };
}

/**
 * Hides everything beside the dialog from assistive technology. Elements that
 * were already hidden are left alone so the restore cannot reveal something the
 * page had deliberately hidden for its own reasons.
 */
export function hideBackgroundFromAssistiveTech(dialogRoot) {
  const parent = dialogRoot && dialogRoot.parentElement;
  if (!parent) return () => {};
  const marked = [];
  Array.from(parent.children).forEach((child) => {
    if (child === dialogRoot) return;
    if (child.getAttribute("aria-hidden") === "true") return;
    child.setAttribute("aria-hidden", "true");
    marked.push(child);
  });
  return () => {
    marked.forEach((element) => element.removeAttribute("aria-hidden"));
  };
}

/**
 * Whether a metadata value has earned a row in the review summary.
 *
 * Zero is a value: a creative with no open threads is reporting a real, useful
 * fact, and dropping that row would make "no comments" indistinguishable from
 * "not measured". Only absent, blank, and the placeholder dashes the metadata
 * extractors emit for "we could not read this" are dropped.
 */
const REVIEW_STAT_PLACEHOLDERS = new Set([
  "-",
  "--",
  "—",
  "n/a",
  "na",
  "null",
  "undefined",
  "unknown",
  "unavailable",
]);

export function hasReviewStatValue(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value === "boolean") return true;
  if (typeof value !== "string") return true;
  const text = value.trim();
  if (!text) return false;
  return !REVIEW_STAT_PLACEHOLDERS.has(text.toLowerCase());
}

/**
 * Returns focus to the element that opened the dialog. Guarded because that
 * element can legitimately be gone by the time the sheet closes — a reviewer
 * who filters the table while the sheet is open unmounts the row they came
 * from, and focus should simply stay put rather than throw.
 */
export function restoreFocus(element) {
  if (!element || typeof element.focus !== "function") return false;
  if (element.isConnected === false) return false;
  element.focus();
  return true;
}
